import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Package, X } from 'lucide-react';
import { posApi } from '@/services/api';

interface ItemServiceSelectProps {
  type: 'Item' | 'Service';
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  /** List parent items only (no variant flattening). For form line picks. */
  parentItemsOnly?: boolean;
  /** Hide the "All Items/Services" row. */
  hideAllOption?: boolean;
  /** Display label when value is set externally (e.g. item_name on a row). */
  label?: string;
  /** Called with the raw API item (or null on clear). Useful when parentItemsOnly. */
  onItemSelect?: (item: any | null) => void;
}

interface SelectOption {
  id: string;
  label: string;
  description?: string;
  item?: any;
}

export default function ItemServiceSelect({
  type,
  value,
  onChange,
  className = '',
  placeholder = 'Search by name or code...',
  parentItemsOnly = false,
  hideAllOption = false,
  label,
  onItemSelect,
}: ItemServiceSelectProps) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(label || '');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!value) {
      setSelectedLabel('');
    } else if (label) {
      setSelectedLabel(label);
    }
  }, [value, label]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data: res } = useQuery({
    queryKey: ['itemsServicesSearch', type, search, parentItemsOnly],
    queryFn: async () => {
      const backendType = type === 'Item' ? 'Product' : type;
      const response = await posApi.getItems({ search, item_type: backendType, limit: 50 });
      return response.data.message;
    },
  });

  const rawItems = res?.items || res || [];

  const options: SelectOption[] = [];
  rawItems.forEach((item: any) => {
    if (parentItemsOnly) {
      options.push({
        id: item.name,
        label: item.item_name || item.name,
        description: item.item_code || item.barcode || undefined,
        item,
      });
      return;
    }
    if (item.has_variants && item.variants && item.variants.length > 0) {
      item.variants.forEach((variant: any) => {
        options.push({
          id: variant.name,
          label: `${item.item_name} - ${variant.variant_name || variant.name}`,
          description: variant.name,
          item,
        });
      });
    } else {
      options.push({
        id: item.name,
        label: item.item_name || item.name,
        description: item.item_code,
        item,
      });
    }
  });

  const handleSelect = (option?: SelectOption) => {
    if (!option) {
      onChange('');
      setSelectedLabel('');
      onItemSelect?.(null);
    } else {
      onChange(option.id);
      setSelectedLabel(option.label);
      onItemSelect?.(option.item ?? null);
    }
    setSearch('');
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {value && selectedLabel ? (
        <div className="input-field flex items-center justify-between gap-2 py-2">
          <span className="truncate text-sm text-gray-900">{selectedLabel}</span>
          <button
            type="button"
            onClick={() => handleSelect()}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
            aria-label={`Clear ${type.toLowerCase()}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              className="input-field pl-9"
              placeholder={placeholder}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
            />
          </div>
          {isOpen && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
              {!hideAllOption && (
                <button
                  type="button"
                  onClick={() => handleSelect()}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100 text-gray-700"
                >
                  All {type}s
                </button>
              )}
              {options.length > 0 ? (
                options.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-50"
                  >
                    <p className="font-medium text-gray-900">{option.label}</p>
                    {option.description && <p className="text-xs text-gray-500">{option.description}</p>}
                  </button>
                ))
              ) : (
                <div className="px-3 py-4 text-sm text-gray-400 text-center flex flex-col items-center">
                  <Package className="w-6 h-6 text-gray-300 mb-2" />
                  No {type.toLowerCase()}s found
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
